/**
 * Kiosk Cart Page - Review and manage order
 * 
 * Phase 1D: Complete rebuild with:
 * - Cart line items with modifiers
 * - Edit item flow (reopens ItemDetailModal)
 * - Promo code entry/validation
 * - Loyalty access
 * - Totals breakdown
 * - Empty cart state
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, Gift, ChevronRight, AlertTriangle } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';
import { formatCurrency } from '../utils/formatCurrency';
import { toast } from 'sonner';

// Components
import { CartItemCard, CartTotals, PromoCodeInput } from '../components/cart';
import { LoyaltyModal } from '../components/loyalty';
import { ItemDetailModal } from '../components/item-detail';
import { IconButton } from '../components/common/TouchButton';

// Theme
import '../styles/kiosk-theme.css';

export default function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    menu,
    orderType,
    tableNumber,
    removeFromCart,
    clearCart,
    customerName,
    setCustomerName,
    customerPhone,
    setCustomerPhone,
    promoCode,
    promoDiscount,
    validatePromoCode,
    clearPromoCode,
    loyaltyPhone,
    loyaltyInfo,
    lookupLoyalty,
    clearLoyalty,
    getCartSubtotal,
    setView,
  } = useKiosk();

  // Local state
  const [editingItem, setEditingItem] = useState(null);
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [showSmsInput, setShowSmsInput] = useState(!!customerPhone);

  // Calculate totals
  const subtotal = getCartSubtotal();
  const discount = promoDiscount?.amount || 0;
  // Tax and the total are the server's: the payment screen prices the order first.
  const total = Math.max(0, Math.round((subtotal - discount) * 100) / 100);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Find menu item by ID (for edit modal)
  const findMenuItem = useCallback((menuItemId) => {
    for (const category of (menu.categories || [])) {
      const item = category.items?.find(i => i.id === menuItemId);
      if (item) return item;
    }
    return null;
  }, [menu.categories]);

  // Navigation
  const handleBack = () => {
    setView('menu');
    navigate('../menu');
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setView('payment');
    navigate('../payment');
  };

  const handleBrowseMenu = () => {
    setView('menu');
    navigate('../menu');
  };

  // Edit item
  const handleEditItem = useCallback((cartItem) => {
    const menuItem = findMenuItem(cartItem.menuItemId);
    if (menuItem) {
      setEditingItem({ cartItem, menuItem });
    } else {
      toast.error('Unable to edit this item');
    }
  }, [findMenuItem]);

  const handleCloseEditModal = useCallback(() => {
    setEditingItem(null);
  }, []);

  const handleEditComplete = useCallback(() => {
    toast.success('Item updated');
    setEditingItem(null);
  }, []);

  // Remove item
  const handleRemoveItem = useCallback((cartItem) => {
    removeFromCart(cartItem.id);
    toast.success(`Removed ${cartItem.name}`);
  }, [removeFromCart]);

  // Promo code
  const handleApplyPromo = useCallback(async (code) => {
    setIsValidatingPromo(true);
    const result = await validatePromoCode(code);
    setIsValidatingPromo(false);
    return result;
  }, [validatePromoCode]);

  const handleRemovePromo = useCallback(() => {
    clearPromoCode();
    toast.success('Promo code removed');
  }, [clearPromoCode]);

  // Loyalty
  const handleLoyaltyLookup = useCallback(async (phone) => {
    return await lookupLoyalty(phone);
  }, [lookupLoyalty]);

  // Get order type display text
  const orderTypeText = orderType === 'dine_in' 
    ? `Dine In${tableNumber ? ` • Table ${tableNumber}` : ''}`
    : 'Take Out';

  // Empty cart state
  if (cart.length === 0) {
    return (
      <div className="kiosk-container min-h-screen flex flex-col">
        {/* Header */}
        <header className="h-20 px-6 flex items-center justify-between border-b border-white/10">
          <IconButton
            icon={ArrowLeft}
            onClick={handleBack}
            aria-label="Go back"
          />
          <h1 className="text-xl font-semibold text-white font-['Outfit']">
            Your Order
          </h1>
          <div className="w-14" />
        </header>

        {/* Empty State */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-24 h-24 rounded-full bg-[#252b33] flex items-center justify-center mb-6">
            <ShoppingBag size={48} className="text-gray-500" />
          </div>
          <h2 className="text-2xl font-semibold text-white mb-3">
            Your cart is empty
          </h2>
          <p className="text-gray-400 text-center max-w-sm mb-8">
            Looks like you haven't added anything yet. Browse our menu to start your order.
          </p>
          <button
            onClick={handleBrowseMenu}
            className="
              h-16 px-8
              flex items-center gap-3
              bg-gradient-to-r from-[#f5a623] to-[#ff6b35]
              text-[#0f1419] text-lg font-semibold
              rounded-2xl
              shadow-lg shadow-[#f5a623]/30
              hover:brightness-110
              active:scale-[0.98]
              transition-all
            "
          >
            <Plus size={22} />
            <span>Browse Menu</span>
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="kiosk-container min-h-screen flex flex-col">
      {/* Header */}
      <header className="h-20 px-6 flex items-center justify-between border-b border-white/10 bg-[#0f1419]">
        <IconButton
          icon={ArrowLeft}
          onClick={handleBack}
          aria-label="Go back to menu"
        />
        <div className="text-center">
          <h1 className="text-xl font-semibold text-white font-['Outfit']">
            Your Order
          </h1>
          <p className="text-sm text-gray-400">{orderTypeText}</p>
        </div>
        <div className="w-14" />
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 overflow-y-auto pb-36">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          
          {/* Cart Items */}
          <section className="space-y-4 mb-8">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <ShoppingBag size={20} />
              <span>Items ({itemCount})</span>
            </h2>
            
            {cart.map((item) => (
              <CartItemCard
                key={item.id}
                item={item}
                onEdit={handleEditItem}
                onRemove={handleRemoveItem}
              />
            ))}

            {/* Add More Items */}
            <button
              onClick={handleBrowseMenu}
              className="
                w-full h-14
                flex items-center justify-center gap-2
                bg-[#1a1f26] border border-dashed border-white/20
                text-gray-400
                rounded-xl
                hover:border-white/40 hover:text-white hover:bg-[#252b33]
                transition-all
              "
            >
              <Plus size={18} />
              <span>Add More Items</span>
            </button>
          </section>

          {/* Promo Code */}
          <section className="mb-6">
            <PromoCodeInput
              appliedCode={promoCode}
              appliedDiscount={promoDiscount}
              onApply={handleApplyPromo}
              onRemove={handleRemovePromo}
              isValidating={isValidatingPromo}
            />
          </section>

          {/* Loyalty */}
          <section className="mb-6">
            <button
              onClick={() => setShowLoyaltyModal(true)}
              className="
                w-full h-14 px-4
                flex items-center justify-between
                bg-[#1a1f26] border border-white/10
                rounded-2xl
                hover:bg-white/5
                transition-colors
              "
            >
              <div className="flex items-center gap-3">
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center
                  ${loyaltyInfo ? 'bg-[#10b981]/20' : 'bg-[#f5a623]/10'}
                `}>
                  <Gift size={18} className={loyaltyInfo ? 'text-[#10b981]' : 'text-[#f5a623]'} />
                </div>
                <div className="text-left">
                  {loyaltyInfo ? (
                    <>
                      <span className="font-medium text-white">
                        {loyaltyInfo.customer_name || 'Rewards Member'}
                      </span>
                      <p className="text-sm text-[#10b981]">
                        {loyaltyInfo.points_balance || 0} points
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-white">Rewards Account</span>
                      <p className="text-sm text-gray-400">Earn points on this order</p>
                    </>
                  )}
                </div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </button>
          </section>

          {/* Customer Info */}
          <section className="mb-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Name for Order
                <span className="text-gray-500 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter your name"
                className="
                  w-full h-14 px-4
                  bg-[#1a1f26] border-2 border-white/10
                  rounded-xl
                  text-white text-base
                  placeholder-gray-500
                  focus:border-[#f5a623] focus:outline-none
                  transition-colors
                "
              />
            </div>

            {/* SMS Notification */}
            <div>
              <button
                onClick={() => setShowSmsInput(!showSmsInput)}
                className={`
                  w-full h-14 px-4
                  flex items-center justify-between
                  bg-[#1a1f26] border border-white/10
                  rounded-xl
                  transition-colors
                  ${showSmsInput ? 'border-[#f5a623]' : 'hover:bg-white/5'}
                `}
              >
                <span className="text-white">
                  📱 Text me when ready
                </span>
                <div className={`
                  w-6 h-6 rounded-md border-2 
                  flex items-center justify-center
                  ${showSmsInput 
                    ? 'bg-[#f5a623] border-[#f5a623]' 
                    : 'border-white/30'
                  }
                `}>
                  {showSmsInput && (
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                      <path d="M1 5L5 9L13 1" stroke="#0f1419" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </button>

              {showSmsInput && (
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = digits;
                    if (digits.length > 3 && digits.length <= 6) {
                      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                    } else if (digits.length > 6) {
                      formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                    }
                    setCustomerPhone(formatted);
                  }}
                  placeholder="(555) 123-4567"
                  className="
                    w-full h-14 px-4 mt-3
                    bg-[#1a1f26] border-2 border-white/10
                    rounded-xl
                    text-white text-base
                    placeholder-gray-500
                    focus:border-[#f5a623] focus:outline-none
                    transition-colors
                  "
                />
              )}
            </div>
          </section>

          {/* Totals */}
          <section className="bg-[#1a1f26] border border-white/10 rounded-2xl p-5">
            <CartTotals
              subtotal={subtotal}
              discount={discount}
              discountLabel={promoCode}
              total={total}
              itemCount={itemCount}
            />
          </section>
        </div>
      </main>

      {/* Sticky Footer */}
      <footer className="
        fixed bottom-0 left-0 right-0 z-40
        bg-[#1a1f26]/98 backdrop-blur-md
        border-t border-white/10
        shadow-[0_-4px_20px_rgba(0,0,0,0.3)]
        px-4 sm:px-6 py-4
      ">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={handleCheckout}
            className="
              w-full h-16
              flex items-center justify-center gap-3
              bg-gradient-to-r from-[#f5a623] to-[#ff6b35]
              text-[#0f1419] text-xl font-semibold
              rounded-2xl
              shadow-lg shadow-[#f5a623]/30
              hover:brightness-110
              active:scale-[0.99]
              transition-all
            "
          >
            <span>Continue to Payment</span>
            <ChevronRight size={24} />
          </button>
        </div>
      </footer>

      {/* Edit Item Modal */}
      {editingItem && (
        <ItemDetailModal
          item={editingItem.menuItem}
          editingCartItem={editingItem.cartItem}
          onClose={handleCloseEditModal}
          onAddToCart={handleEditComplete}
        />
      )}

      {/* Loyalty Modal */}
      <LoyaltyModal
        isOpen={showLoyaltyModal}
        onClose={() => setShowLoyaltyModal(false)}
        onLookup={handleLoyaltyLookup}
        currentPhone={loyaltyPhone}
        currentLoyaltyInfo={loyaltyInfo}
      />
    </div>
  );
}
